import axios from 'axios';
import {
  fetch_scoped_entity_types,
  get_entity_type_name,
  get_request_config,
  normalize_key,
  normalize_text,
  read_session_current_user_id,
  to_number,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_helpers';
import { status_dot_class } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_formatters';
import { status_dot_style } from '@/views/x-verdict_views/CaseOwner/AllCases/case_state';
import {
  assign_requestor_application_users,
  create_requestor_case,
  fetch_requestor_application_forms,
  update_requestor_application_status,
} from '@/services/requestor_portal_service';
import { fetch_assign_form_status_data, fetch_user_roles } from '@/services/workflow_service';
import { GET_ROLE_GROUPS, GET_USERS } from '@/utils/system_constants/constant.js';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import {
  build_intake_pipeline_steps,
  build_intake_summary_metrics,
  build_main_db_case,
  build_register_form,
  fetch_intake_cases,
  map_cases_to_inbound_requests,
  resolve_entity_type_option,
} from '../intake_case_queue_service';
import {
  created_manual_case,
  resolve_manual_case_form_ids,
  validate_manual_case_registration,
} from '../manual_case_form_helpers';

const INFO_CALLOUT = 'X-VERDICT ships as a generic case engine. Categories, role labels and workflows are per-tenant configuration on the same shell.';

const CHANNELS = [
  { key: 'all', label: 'All channels' },
  { key: 'portal', label: 'Portal' },
  { key: 'call', label: 'Call centre' },
  { key: 'email', label: 'Email' },
  { key: 'walk-in', label: 'Walk-in' },
];

const PIPELINE_STEPS = build_intake_pipeline_steps();
const SUMMARY_METRICS = build_intake_summary_metrics();
const REGISTERED_CASE_IDS_KEY = 'intake_registered_case_ids';
const REGISTERED_INBOUND_IDS_KEY = 'intake_registered_inbound_ids';
const REGISTERED_CASE_RECORDS_KEY = 'intake_registered_case_records';

function build_type_badge_class(value = '') {
  const normalized = normalize_key(value);
  if (normalized.includes('claim')) return 'bg-danger-subtle text-danger-emphasis';
  if (normalized.includes('legal')) return 'bg-secondary-subtle text-secondary-emphasis';
  if (normalized.includes('oversight')) return 'bg-warning-subtle text-warning-emphasis';
  if (normalized.includes('hr')) return 'bg-success-subtle text-success-emphasis';
  if (normalized.includes('safety')) return 'bg-danger-subtle text-danger-emphasis';
  return 'bg-primary-subtle text-primary-emphasis';
}

function build_channel_badge_class(value = '') {
  const normalized = normalize_key(value);
  if (normalized.includes('call')) return 'bg-warning-subtle text-warning-emphasis';
  if (normalized.includes('email')) return 'bg-primary-subtle text-primary-emphasis';
  if (normalized.includes('walk')) return 'bg-secondary-subtle text-secondary-emphasis';
  return 'bg-primary-subtle text-primary-emphasis';
}

function build_flag_badge_class(flag = '', fallback = '') {
  const normalized = normalize_key([flag, fallback].filter(Boolean).join(' '));
  if (!normalized) return 'company-case-sla-badge is-closed';
  if (normalized.includes('urgent')) return 'company-case-sla-badge is-danger';
  if (normalized.includes('warning') || normalized.includes('duplicate')) return 'company-case-sla-badge is-warning';
  return 'company-case-sla-badge is-active';
}

function normalize_inbound_request(item = {}) {
  return {
    ...item,
    channel_badge_class: build_channel_badge_class(item.channel || item.channel_label),
    suggested_type_badge_class: build_type_badge_class(item.suggested_type),
    flag_badge_class: build_flag_badge_class(item.flag, item.flag_class),
  };
}

function normalize_live_case(item = {}) {
  return {
    ...item,
    type_badge_class: build_type_badge_class(item.type || get_entity_type_name(item)),
    stage_dot_class: status_dot_class(item.stage || item.status),
    status_dot_style: status_dot_style(item.status_color),
  };
}


function case_detail_query(case_item = {}) {
  return Object.fromEntries([
    ['application_id', case_item?.application_id],
    ['entity_profile_id', case_item?.entity_profile_id],
    ['entity_type_id', case_item?.entity_type_id],
  ].filter(([, value]) => Boolean(value)));
}

function normalized_text(value = '') {
  return String(value || '').trim().toLowerCase();
}

function manual_case_form() {
  return {
    title: 'Register · inbound request',
    subtitle: 'Portal · Just now',
    description: '',
    requester: '',
    contact: '',
    location: '',
    case_type: '',
  };
}

function read_storage_ids(key) {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write_storage_ids(key, values = []) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(Array.from(new Set(values.map(String)))));
}

function append_storage_id(key, value) {
  if (!value) return;
  write_storage_ids(key, [...read_storage_ids(key), String(value)]);
}

function read_storage_records(key) {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write_storage_records(key, values = []) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(key, JSON.stringify(values));
}

function case_record_key(case_item = {}) {
  return String(case_item?.application_id || case_item?.case_code || '');
}

function merge_case_records(primary_cases = [], secondary_cases = []) {
  const merged = new Map();

  [...secondary_cases, ...primary_cases].forEach((case_item) => {
    const key = case_record_key(case_item);
    if (!key) return;

    const existing = merged.get(key) || {};
    merged.set(key, {
      ...existing,
      ...case_item,
    });
  });

  return Array.from(merged.values());
}

function upsert_registered_case_record(case_item = {}) {
  const key = case_record_key(case_item);
  if (!key) return;

  const existing_records = read_storage_records(REGISTERED_CASE_RECORDS_KEY);
  const next_records = merge_case_records([case_item], existing_records);
  write_storage_records(REGISTERED_CASE_RECORDS_KEY, next_records);
}

function is_email(value = '') {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function parse_response_data(response) {
  const raw_data = response?.data ?? response ?? {};
  if (typeof raw_data !== 'string') return raw_data;

  try {
    const parsed = JSON.parse(raw_data);
    return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
  } catch {
    return {};
  }
}

function response_items(response, keys = []) {
  const parsed = parse_response_data(response);
  const nested = parse_response_data(parsed?.data);
  const candidates = [
    nested,
    parsed?.data,
    ...keys.map((key) => parsed?.[key]),
    parsed?.results,
    parsed,
  ];
  return candidates.find(Array.isArray) || [];
}

function uniq_by(items = [], key_builder = (item) => item) {
  const seen = new Set();
  return items.filter((item) => {
    const key = key_builder(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function workflow_status_option(status = {}) {
  const id = to_number(status?.id || status?.status_id);
  const label = normalize_text(status?.status || status?.status_name || status?.label || status?.name);
  const order = Number(status?.order_number ?? status?.order ?? status?.priority ?? 0);
  const is_final = Boolean(status?.is_final);
  return id && label ? { id, label, order, is_final } : null;
}

function normalized_route_role_label(value = '') {
  const label = normalize_text(value);
  return normalize_key(label) === 'system-owner' ? 'Coordinator' : label;
}

function role_option(role = {}) {
  const id = to_number(role?.id);
  const label = normalized_route_role_label(role?.role || role?.role_name || role?.name);
  const group_id = to_number(role?.group_id || role?.group?.id || role?.group);
  const group_name = normalize_text(role?.group_name || role?.group?.name);
  return id && label ? { id, label, group_id, group_name } : null;
}

function route_role_key(role = {}) {
  return normalize_key(role?.label);
}

function route_group_option(role = {}) {
  if (!role?.group_id || !role?.group_name) return null;
  return {
    id: role.group_id,
    label: role.group_name,
    role_id: role.id,
  };
}

function user_option(user = {}) {
  const user_id = to_number(user?.user_id || user?.id || user?.user?.id);
  const role = normalized_route_role_label(user?.user?.role?.role || user?.role_name || user?.role);
  const group_id = to_number(user?.user?.role?.group_id || user?.group_id);
  const group_name = normalize_text(user?.user?.role?.group_name || user?.group_name);
  const name = normalize_text([
    user?.user?.first_name,
    user?.user?.last_name,
  ].filter(Boolean).join(' ')) || normalize_text(
    user?.user?.email || user?.user?.username || user?.name
  );

  return user_id && name
    ? { user_id, name, role, group_id, group_name }
    : null;
}

function assignment_label(user = {}) {
  return [user.name, user.role].filter(Boolean).join(' - ');
}

function route_note_label(names = [], count = 0) {
  const selected_names = names.filter(Boolean).join(', ');
  if (selected_names) return `Sent to ${selected_names}.`;
  return count === 1 ? 'Sent to one team member.' : `Sent to ${count} team members.`;
}

function live_case_status_filter_option(case_item = {}) {
  const label = normalize_text(case_item?.stage || case_item?.status);
  if (!label) return null;

  return {
    key: normalize_key(label),
    label,
  };
}

function inbound_status_filter_option(request_item = {}) {
  const label = normalize_text(request_item?.status || request_item?.flag);
  if (!label) return null;

  return {
    key: normalize_key(label),
    label,
  };
}

function application_form_id(record = {}) {
  return to_number(record?.form_id || record?.form?.id || record?.id);
}

function unique_statuses(statuses = []) {
  return uniq_by(statuses, (status) => String(status?.id)).sort((left, right) => {
    const left_order = Number(left?.order || 0);
    const right_order = Number(right?.order || 0);
    if (left_order !== right_order) return left_order - right_order;
    return String(left?.label || '').localeCompare(String(right?.label || ''));
  });
}

function assigned_statuses_from_response(response) {
  return response_items(response, ['assigned_statuses', 'selected_statuses']);
}

export default {
  name: 'IntakeDesk',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => ({
    active_channel: 'all',
    active_inbound_status: 'all',
    active_live_case_status: 'all',
    search_term: '',
    channels: CHANNELS,
    pipeline_steps: PIPELINE_STEPS,
    summary_metrics: SUMMARY_METRICS,
    inbound_requests: [],
    live_cases: [],
    info_callout: INFO_CALLOUT,
    show_manual_case_modal: false,
    manual_case: manual_case_form(),
    manual_case_error: '',
    selected_register_request: null,
    case_type_options: [],
    project_id: null,
    show_route_case_modal: false,
    route_case: null,
    route_priority: '',
    route_destination: '',
    route_group: '',
    route_status_options: [],
    route_role_options: [],
    route_user_options: [],
    route_selected_user_ids: [],
    route_select_all_users: false,
    route_error: '',
    route_loading: false,
    submitting_route_case: false,
    submitting_manual_case: false,
  }),
  created() {
    this.project_id = this.$route?.query?.project_id || this.$route?.query?.projectId || null;
    this.load_case_type_options();
    this.load_live_cases();
  },
  computed: {
    project_query() {
      const project_id = this.$route?.query?.project_id || this.$route?.query?.projectId;
      return project_id ? { project_id: String(project_id) } : {};
    },
    should_open_manual_case() {
      return String(this.$route?.query?.open_manual_case || '') === '1';
    },
    filtered_inbound_requests() {
      const search_value = normalized_text(this.search_term);
      return this.inbound_requests.filter((item) => {
        const matches_channel = this.active_channel === 'all' || item.channel === this.active_channel;
        if (!matches_channel) return false;

        if (
          this.active_inbound_status !== 'all'
          && normalize_key(item?.status || item?.flag) !== this.active_inbound_status
        ) {
          return false;
        }

        if (!search_value) return true;

        return [
          item.title,
          item.detail,
          item.channel_label,
          item.suggested_type,
          item.flag,
        ].some((value) => normalized_text(value).includes(search_value));
      });
    },
    display_inbound_status_filters() {
      const status_filters = uniq_by(
        this.inbound_requests
          .map(inbound_status_filter_option)
          .filter(Boolean),
        (status) => status.key
      );

      return [
        { key: 'all', label: 'Filter by status' },
        ...status_filters,
      ];
    },
    display_live_case_status_filters() {
      const status_filters = uniq_by(
        this.live_cases
          .map(live_case_status_filter_option)
          .filter(Boolean),
        (status) => status.key
      );

      return [
        { key: 'all', label: 'Filter by status' },
        ...status_filters,
      ];
    },
    filtered_live_cases() {
      if (this.active_live_case_status === 'all') {
        return this.live_cases;
      }

      return this.live_cases.filter((item) =>
        normalize_key(item?.stage || item?.status) === this.active_live_case_status
      );
    },
    route_case_title() {
      const case_code = this.route_case?.case_code || 'Case';
      return `Triage & route - ${case_code}`;
    },
    route_case_subtitle() {
      return [
        this.route_case?.stage || this.route_case?.status || 'New case',
        this.route_case?.type || this.route_case?.domain || 'Service',
      ].filter(Boolean).join(' - ');
    },
    route_destination_options() {
      return uniq_by(this.route_role_options, route_role_key);
    },
    selected_route_role() {
      return this.route_destination_options.find((role) => String(role.id) === String(this.route_destination)) || null;
    },
    available_route_groups() {
      const selected_role = this.selected_route_role;
      if (!selected_role) return [];

      return uniq_by(
        this.route_role_options
          .filter((role) => route_role_key(role) === route_role_key(selected_role))
          .map(route_group_option)
          .filter(Boolean),
        (group) => String(group.id)
      );
    },
    selected_route_group() {
      return this.available_route_groups.find((group) => String(group.id) === String(this.route_group)) || null;
    },
    available_route_users() {
      const selected_role = this.selected_route_role;
      const selected_group = this.selected_route_group;
      if (!selected_role || !selected_group) return [];

      const is_coordinator_destination = route_role_key(selected_role) === 'coordinator';
      const current_user_id = read_session_current_user_id();

      return this.route_user_options.filter((user) =>
        normalize_key(user.role) === route_role_key(selected_role)
        && Number(user.group_id) === Number(selected_group.id)
        && (!is_coordinator_destination || Number(user.user_id) !== Number(current_user_id))
      );
    },
    selected_route_status() {
      return this.route_status_options.find((status) => String(status.id) === String(this.route_priority)) || null;
    },
    can_save_route_case() {
      return Boolean(
        this.route_case
        && this.selected_route_status
        && this.selected_route_role
        && this.selected_route_group
        && this.route_selected_user_ids.length
        && !this.submitting_route_case
      );
    },
    selected_manual_case_type() {
      return this.case_type_options.find(
        (option) => String(option.id) === String(this.manual_case.case_type)
      ) || null;
    },
    can_submit_manual_case() {
      return !validate_manual_case_registration(this.manual_case, this.selected_manual_case_type)
        && !this.submitting_manual_case;
    },
  },
  watch: {
    should_open_manual_case: {
      immediate: true,
      handler(should_open) {
        this.show_manual_case_modal = should_open;
      },
    },
  },
  methods: {
    assignment_label,
    async load_case_type_options() {
      try {
        const { entity_types } = await fetch_scoped_entity_types(this.project_id);
        this.case_type_options = entity_types
          .map((entity_type) => ({
            id: String(entity_type?.id || ''),
            label: get_entity_type_name(entity_type),
            form_ids: Array.isArray(entity_type?.form_ids) ? entity_type.form_ids : [],
          }))
          .filter((option) => option.id && option.label);
      } catch {
        this.case_type_options = [];
      }
    },
    async load_live_cases() {
      try {
        const cases = await fetch_intake_cases(this.project_id);
        const registered_case_ids = new Set(read_storage_ids(REGISTERED_CASE_IDS_KEY));
        const registered_inbound_ids = new Set(read_storage_ids(REGISTERED_INBOUND_IDS_KEY));
        const registered_case_records = read_storage_records(REGISTERED_CASE_RECORDS_KEY);
        const matched_live_cases = cases.filter((item) => {
          const application_id = String(item?.application_id || '');
          const case_code = String(item?.case_code || '');
          return registered_case_ids.has(application_id) || registered_case_ids.has(case_code);
        });
        const merged_live_cases = merge_case_records(matched_live_cases, registered_case_records);

        this.inbound_requests = map_cases_to_inbound_requests(cases)
          .map(normalize_inbound_request)
          .filter((item) => !registered_inbound_ids.has(String(item.id)));
        this.live_cases = merged_live_cases.map(normalize_live_case);
        this.pipeline_steps = build_intake_pipeline_steps(cases, this.inbound_requests);
        this.summary_metrics = build_intake_summary_metrics(cases);
      } catch {
        this.inbound_requests = [];
        this.live_cases = read_storage_records(REGISTERED_CASE_RECORDS_KEY).map(normalize_live_case);
        this.pipeline_steps = build_intake_pipeline_steps();
        this.summary_metrics = build_intake_summary_metrics();
      }
    },
    set_channel(channel_key) {
      this.active_channel = channel_key;
    },
    set_inbound_status_filter(status_key) {
      this.active_inbound_status = normalize_key(status_key) || 'all';
    },
    set_live_case_status_filter(status_key) {
      this.active_live_case_status = normalize_key(status_key) || 'all';
    },
    open_register_case(request_item) {
      this.selected_register_request = request_item;
      this.manual_case = build_register_form(request_item);
      const matched_case_type = resolve_entity_type_option(request_item, this.case_type_options);
      this.manual_case.case_type = matched_case_type?.id || '';
      this.manual_case_error = '';
      this.show_manual_case_modal = true;
    },
    open_manual_case_modal() {
      this.selected_register_request = null;
      this.manual_case = manual_case_form();
      this.manual_case_error = '';
      this.$router.push({
        name: this.$route.name,
        query: {
          ...this.$route.query,
          open_manual_case: '1',
        },
      });
    },
    close_manual_case_modal() {
      if (this.submitting_manual_case) return;
      this.dismiss_manual_case_modal();
    },
    dismiss_manual_case_modal() {
      const next_query = { ...this.$route.query };
      delete next_query.open_manual_case;
      this.show_manual_case_modal = false;
      this.selected_register_request = null;
      this.manual_case = manual_case_form();
      this.manual_case_error = '';
      this.$router.replace({
        name: this.$route.name,
        query: next_query,
      });
    },
    async submit_manual_case() {
      if (this.submitting_manual_case) return;

      const selected_case_type = this.case_type_options.find(
        (option) => String(option.id) === String(this.manual_case.case_type)
      );
      const entity_type_id = to_number(selected_case_type?.id);
      const validation_error = validate_manual_case_registration(this.manual_case, selected_case_type);
      if (validation_error) {
        this.manual_case_error = validation_error;
        return;
      }

      this.submitting_manual_case = true;
      this.manual_case_error = '';
      try {
        const source_request = this.selected_register_request || {
          id: `manual-${Date.now()}`,
          title: this.manual_case.description || this.manual_case.requester || 'Registered case',
          case_code: '',
          application_id: null,
          channel_label: 'Walk-in',
        };
        const form_ids = await resolve_manual_case_form_ids({
          entity_type_id,
          project_id: this.project_id,
          fallback_form_ids: selected_case_type.form_ids,
        });
        const payload = {
          entity_name: this.manual_case.description || source_request.title || this.manual_case.requester,
          entity_type_id,
          form_ids,
          run_onboarding: true,
          source_channel: source_request.channel_label || 'Portal',
          user_email: is_email(this.manual_case.contact) ? this.manual_case.contact : undefined,
          send_user_email: false,
          ...(this.project_id ? { project_id: Number(this.project_id) } : {}),
        };

        const create_data = await create_requestor_case(payload);
        const created_case = created_manual_case(create_data, entity_type_id);
        const created_application_id = created_case.application_id;
        const created_case_code = created_case.case_code;

        append_storage_id(
          REGISTERED_CASE_IDS_KEY,
          created_application_id || created_case_code
        );
        if (this.selected_register_request) {
          append_storage_id(REGISTERED_INBOUND_IDS_KEY, source_request.id);
        }

        const registered_case_record = normalize_live_case({
          ...build_main_db_case(
            {
              ...source_request,
              case_code: created_case_code || source_request.case_code,
              application_id: created_application_id || source_request.application_id,
            },
            this.manual_case,
            selected_case_type?.label
          ),
          case_code: created_case_code || source_request.case_code || source_request.id,
          application_id: created_application_id || source_request.application_id || null,
          entity_profile_id: created_case.entity_profile_id,
          entity_type_id,
        });
        upsert_registered_case_record(registered_case_record);

        await this.load_live_cases();

        this.dismiss_manual_case_modal();
        if (created_case.application_id && created_case.case_code) {
          this.go_to_case_detail(created_case);
        }
      } finally {
        this.submitting_manual_case = false;
      }
    },
    async load_route_modal_options() {
      this.route_loading = true;
      this.route_error = '';

      try {
        const application_forms = this.route_case?.application_id
          ? await fetch_requestor_application_forms(this.route_case.application_id)
          : [];
        const form_ids = [...new Set(application_forms.map(application_form_id).filter(Boolean))];
        const status_groups = await Promise.all(
          form_ids.map((form_id) => fetch_assign_form_status_data({ form_id }))
        );

        const [roles, role_groups_response, users_response] = await Promise.all([
          fetch_user_roles(this.project_id ? { project_id: this.project_id } : {}),
          axios.get(GET_ROLE_GROUPS, get_request_config()),
          axios.get(GET_USERS, get_request_config({
            params: {
              page: 1,
              page_size: 500,
              ...(this.project_id ? { project_id: this.project_id } : {}),
            },
          })),
        ]);

        this.route_status_options = unique_statuses(
          status_groups
            .flatMap((response) => assigned_statuses_from_response(response))
            .map(workflow_status_option)
            .filter(Boolean)
        );

        const role_groups = response_items(role_groups_response, ['role_groups'])
          .map((group) => ({
            id: to_number(group?.id || group?.group_id),
            label: normalize_text(group?.name || group?.group_name),
          }))
          .filter((group) => group.id && group.label);

        this.route_role_options = response_items(roles, ['roles'])
          .map(role_option)
          .filter(Boolean)
          .filter((role) => role.group_id)
          .map((role) => {
            const matched_group = role_groups.find((group) => group.id === role.group_id);
            return matched_group ? { ...role, group_name: matched_group.label } : role;
          });

        this.route_user_options = response_items(users_response, ['users'])
          .map(user_option)
          .filter(Boolean);

        if (!this.selected_route_status && this.route_status_options[0]) {
          this.route_priority = String(this.route_status_options[0].id);
        }

        if (!this.selected_route_role && this.route_destination_options[0]) {
          this.route_destination = String(this.route_destination_options[0].id);
        }

        if (!this.selected_route_group && this.available_route_groups[0]) {
          this.route_group = String(this.available_route_groups[0].id);
        }

        if (!this.route_status_options.length) {
          this.route_error = 'No workflow statuses are configured for this case.';
        }
      } catch (error) {
        this.route_error = error?.response?.data?.message || error?.message || 'Failed to load routing options.';
      } finally {
        this.route_loading = false;
      }
    },
    async route_to_case(item) {
      if (!item?.application_id) {
        this.go_to_case_detail(item);
        return;
      }

      this.route_case = item;
      this.route_priority = '';
      this.route_destination = '';
      this.route_group = '';
      this.route_selected_user_ids = [];
      this.route_select_all_users = false;
      this.route_error = '';
      this.show_route_case_modal = true;
      await this.load_route_modal_options();
    },
    close_route_case_modal() {
      if (this.submitting_route_case) return;
      this.dismiss_route_case_modal();
    },
    dismiss_route_case_modal() {
      this.show_route_case_modal = false;
      this.route_case = null;
      this.route_priority = '';
      this.route_destination = '';
      this.route_group = '';
      this.route_selected_user_ids = [];
      this.route_select_all_users = false;
      this.route_error = '';
    },
    sync_route_group_selection() {
      if (!this.selected_route_group && this.available_route_groups[0]) {
        this.route_group = String(this.available_route_groups[0].id);
      }
      if (!this.available_route_groups.length) {
        this.route_group = '';
      }
      this.route_selected_user_ids = [];
      this.route_select_all_users = false;
    },
    sync_route_user_selection() {
      const available_ids = new Set(this.available_route_users.map((user) => String(user.user_id)));
      this.route_selected_user_ids = this.route_selected_user_ids.filter((user_id) => available_ids.has(String(user_id)));
      this.route_select_all_users = this.available_route_users.length > 0
        && this.route_selected_user_ids.length === this.available_route_users.length;
    },
    toggle_all_route_users() {
      this.route_selected_user_ids = this.route_select_all_users
        ? this.available_route_users.map((user) => String(user.user_id))
        : [];
    },
    async route_selected_case() {
      if (!this.can_save_route_case || !this.route_case?.application_id) {
        this.route_error = 'Select a workflow status, role group, and at least one user before routing.';
        return;
      }

      this.submitting_route_case = true;
      this.route_error = '';

      try {
        const application_id = Number(this.route_case.application_id);
        const entity_profile_id = Number(this.route_case.entity_profile_id) || null;
        const role_id = Number(this.selected_route_group?.role_id || this.selected_route_role?.id);
        const selected_user_ids = this.route_selected_user_ids.map((user_id) => Number(user_id)).filter(Boolean);
        const selected_status = this.selected_route_status;
        const selected_user_names = selected_user_ids
          .map((user_id) => this.available_route_users.find((user) => Number(user.user_id) === user_id)?.name)
          .filter(Boolean);
        const routed_to_label = selected_user_names.join(', ') || 'Assigned';

        await assign_requestor_application_users({
          application_id,
          assignments: selected_user_ids.map((user_id) => ({
            user_id,
            role_id,
          })),
        });

        if (selected_status?.label) {
          await update_requestor_application_status({
            application_id,
            entity_profile_id,
            status: selected_status.label,
            note: route_note_label(selected_user_names, selected_user_ids.length),
            action_taken: 'Case routed',
          });
        }

        this.live_cases = this.live_cases.map((item) => (
          item.case_code === this.route_case.case_code
            ? normalize_live_case({
              ...item,
              routed_to: routed_to_label,
              owner: routed_to_label,
              stage: selected_status?.label || item.stage,
            })
            : item
        ));
        upsert_registered_case_record(
          this.live_cases.find((item) => item.case_code === this.route_case.case_code) || {}
        );
        this.dismiss_route_case_modal();
      } catch (error) {
        this.route_error = error?.response?.data?.message || error?.message || 'Failed to route case.';
      } finally {
        this.submitting_route_case = false;
      }
    },
    go_to_case_detail(case_item) {
      this.$router.push({
        name: 'ViewCaseDetails',
        params: { caseCode: case_item.case_code },
        query: {
          ...this.project_query,
          ...case_detail_query(case_item),
        },
      });
    },
  },
};
